// src/components/Procurement/ManageLandedCosts.jsx
// Prompt 206 — "Manage Landed Costs". Reusable panel for the GIT page (and future v6 Order page).
//
// Layout: (1) list of existing LC entries for the order (from the E10 engine, grain_type='lc_line'),
// each with [Adjust] [Cancel]; (2) the existing "Add new" form (LandedCostEntryPanel) below.
//
// BACKEND (206 §9): ADJUST edits the LC's new REMAINING via POST /api/landed-costs/adjust-impact (gate)
// + POST /api/landed-costs/adjust (increase → holding-IN; decrease → sf110PostLcDisposalLeg: credit →
// cancellation/AP auto-reverse, cash → refund/writeoff paid_choice). Every post is SR_A1-gated + reverts.
// CANCEL = adjust to new_remaining 0 (zeroes the holding, posts the disposal leg, runs the §9 gate).
// Absorbed is immutable — a decrease can't go below lc_absorbed (new_remaining ≥ 0 enforces it).

import { formatDate } from '../../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import LandedCostEntryPanel from './LandedCostEntryPanel';
import Combobox from '../Combobox';
import { PaymentBadge } from './StatusBadge';
import { lcRefundBadge } from '../../lib/lcDisposition';
import { lineText } from '../../shell/ActionIcons';
import { Emo, emoText } from '../../shell/EmojiIcons';
import { useTranslation } from 'react-i18next';
import { Field } from '../form/FormField';
import Badge from '../Badge';
import useActionLabel from '../../i18n/useActionLabel';
import './landedCost.css';
import { TextInput } from '../inputs/Input';

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 }));

// 703 (4d): the LOCAL PAY_BADGE map is DELETED. It carried only paid/partial/unpaid, so BOTH
// `refund_due` and `void` fell through to a red "Unpaid" — a we-owe-you LC and a cancelled LC rendered
// identically as debt (700 A2). This now routes through the shared PaymentBadge + lcRefundBadge rail,
// the same path OrderTabs.jsx:465 uses, so every P1 value renders correctly and in one vocabulary.

function Stat({ label, value, tone }) {
  return (
    <div style={{ minWidth: 96 }}>
      <span className="avf-lab">{emoText(label)}</span>
      <div className={`lc-stat-v ${tone || ''}`.trim()}>{value}</div>
    </div>
  );
}

export default function ManageLandedCosts({
  operationId,
  gitItems = [],
  partnersMap = {},
  onClose,
  onSuccess,   // called after any successful post (adjust or add) so the parent can refresh
  addOnly: addOnlyProp = false,   // when true, render ONLY the "Add a new landed cost" form (the list lives elsewhere)
  mode = null,   // door param: 'adjust' | 'cancel' opens that flow for landedCostId once the rows load; 'add' = addOnly
  landedCostId = null,
  mixedUnitOverride = null,   // authoritative mixed-unit flag from the order page (reliable units)
}) {
  const addOnly = addOnlyProp || mode === 'add';
  const { t } = useTranslation('landed'); const al = useActionLabel();
  const [rows, setRows] = useState(null);   // lc_line rows | null (loading) | [] (none)
  const [loadErr, setLoadErr] = useState(null);
  // Whether this order has mixed item units (e.g. kg + dose) → "By Qty" allocation is invalid.
  const [mixedUnit, setMixedUnit] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null); // landed_cost_id currently being adjusted
  // 206 §9: the user edits the LC's NEW REMAINING (holding). new_total = absorbed + new_remaining.
  const [adjNewRem, setAdjNewRem] = useState('');
  const [adjImpact, setAdjImpact] = useState(null);   // GET /landed-costs/adjust-impact result
  const [adjPaidChoice, setAdjPaidChoice] = useState(''); // 'refund_receivable' | 'writeoff' (cash decrease)
  const [adjReason, setAdjReason] = useState('');   // 208: optional §9 reason threaded into the adjust body
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjErr, setAdjErr] = useState(null);
  const [cancelFor, setCancelFor] = useState(null); // lc_line row being cancel-confirmed
  const [cancelImpact, setCancelImpact] = useState(null); // adjust-impact for new_remaining=0
  const [cancelPaidChoice, setCancelPaidChoice] = useState(''); // cash LC cancel → refund/writeoff
  const [cancelBusy, setCancelBusy] = useState(false);
  const [methodBusy, setMethodBusy] = useState(null); // landed_cost_id whose method is changing

  // ── Entry-point modes (206 follow-up #3) ──────────────────────────────────────────────────────
  // (a) GIT expanded order → `operationId` is passed (locked to that order).
  // (b) header "+ Data Entry" toggle / (c) Data Entry > Landed Costs → no order passed: the user picks
  //     one from a dropdown. Same shared component; standalone self-fetches the order list + the
  //     partners/gitItems the GIT host would otherwise pass in.
  const standalone = !operationId;
  const [pickedOpId, setPickedOpId] = useState('');
  const [lcCountByOp, setLcCountByOp] = useState({});   // op_id -> # existing landed costs, shown in the picker
  const [operations, setOperations] = useState([]);
  const [intPartnersMap, setIntPartnersMap] = useState({});
  const [intGitItems, setIntGitItems] = useState([]);
  const effectiveOpId = operationId || pickedOpId;
  const effPartnersMap = (partnersMap && Object.keys(partnersMap).length) ? partnersMap : intPartnersMap;
  const effGitItems = (gitItems && gitItems.length) ? gitItems : intGitItems;

  const needGitItems = !(gitItems && gitItems.length);
  const needPartners = !(partnersMap && Object.keys(partnersMap).length);
  useEffect(() => {
    // Self-fetch whatever the host didn't pass: the order list (standalone only), plus gitItems /
    // partners when absent (the Data Entry purchase branch passes an operationId but neither).
    if (standalone || needGitItems) {
      api.getPlannedTransactions({ only: 'git' }).then(data => {   // fb-188: GIT section only (fast)
        const git = data?.goods_in_transit || [];
        if (needGitItems) setIntGitItems(git);
        if (standalone) {
          const byOp = {};
          for (const r of git) {
            if (!byOp[r.operation_id]) byOp[r.operation_id] = { operation_id: r.operation_id, date: r.date || '', partner_name: r.partner_name || '', items: new Set() };
            if (r.item_name) byOp[r.operation_id].items.add(r.item_name);
          }
          const opsSorted = Object.values(byOp).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          setOperations(opsSorted);
          // LC count per order comes with the GIT rows (landed_costs_count) - no per-order /landed-costs call needed (was ~50 calls).
          const cnt = {}; for (const r of git) { if (r.operation_id) cnt[r.operation_id] = Math.max(cnt[r.operation_id] || 0, Number(r.landed_costs_count) || 0); }
          setLcCountByOp(cnt);
        }
      }).catch(() => {});
    }
    if (needPartners) api.getPartners().then(ps => { const m = {}; for (const p of (ps || [])) m[p.id] = p; setIntPartnersMap(m); }).catch(() => {});
  }, [standalone, needGitItems, needPartners]);

  // The engine view (lc_line) has the computed fields (lc_current/absorbed/holding/payment) but NOT
  // account_from_id / allocation_method (needed to post an adjustment). Fetch the raw landed_costs rows
  // (GET /api/landed-costs → select *) and merge by id === landed_cost_id.
  const load = useCallback(() => {
    if (!effectiveOpId) { setRows(null); return; }
    setLoadErr(null);
    Promise.all([
      api.getPurchaseOrder({ operation_id: effectiveOpId, grain_type: 'lc_line' }),
      api.request(`/landed-costs?operation_id=${encodeURIComponent(effectiveOpId)}`).catch(() => ({ landed_costs: [] })),
      // Engine goods_lines carry per-line units → detect a mixed-unit order (kg + dose) so the
      // "By Qty" allocation buttons can be disabled (the backend also 400s mixed-unit by-quantity).
      api.getProcurementOrders({ include_received: true }).catch(() => null),
    ])
      .then(([engineRows, raw, engine]) => {
        const rawById = {};
        for (const r of (raw?.landed_costs || [])) rawById[r.id] = r;
        // v_purchase_order (db/277) exposes cash-aware lc_amount_paid / lc_amount_owed / lc_payment_status
        // directly (a CASH LC paid at creation now reads 'paid'), so no client overlay is needed. The
        // engine fetch below is kept only for mixed-unit detection on the goods lines.
        const ord = (engine?.orders || []).find(o => o.operation_id === effectiveOpId);
        const merged = (Array.isArray(engineRows) ? engineRows : []).map(lc => {
          const src = rawById[lc.landed_cost_id] || {};
          return {
            ...lc,
            account_from_id: src.account_from_id || null, lc_allocation_method: src.allocation_method || null, notes: src.notes || null,
          };
        });
        setRows(merged);
        const units = [...new Set((ord?.goods_lines || []).map(l => (l.unit || '').trim()).filter(Boolean))];
        setMixedUnit(units.length > 1);
      })
      .catch(e => { setRows([]); setLoadErr(e.message || 'Failed to load landed costs.'); });
  }, [effectiveOpId]);

  useEffect(() => { load(); }, [load]);

  // Door entry (shell 'landed' drawer with mode + landedCostId): open that LC's Adjust / Cancel flow once, after the rows load.
  const autoOpened = React.useRef(false);
  useEffect(() => {
    if (autoOpened.current || !rows || !landedCostId || (mode !== 'adjust' && mode !== 'cancel')) return;
    const lc = rows.find(r => r.landed_cost_id === landedCostId);
    if (!lc) return;
    autoOpened.current = true;
    if (mode === 'cancel') startCancel(lc); else openAdjust(lc);
  }, [rows]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel = adjust the LC's remaining to 0 (zeroes holding, posts the disposal leg, §9-gated). Same
  // endpoint/flow as a decrease-to-0 adjustment; cash LCs need a refund/writeoff choice.
  async function startCancel(lc) {
    setCancelFor(lc); setAdjustFor(null); setCancelPaidChoice(''); setCancelImpact(null); setAdjErr(null);
    try { setCancelImpact(await api.getLcAdjustImpact(lc.landed_cost_id, 0)); } catch { /* impact optional */ }
  }
  async function doCancel(lc) {
    if (hasPaidDecrease(cancelImpact) && !cancelPaidChoice) { setAdjErr('This landed cost is paid — choose Collect, Cancel, or Write off.'); return; }
    setCancelBusy(true); setAdjErr(null);
    try {
      const { ok, status, data } = await api.adjustLandedCost({
        landed_cost_id: lc.landed_cost_id, new_remaining: 0,
        ...(cancelPaidChoice ? { lc_disposition: cancelPaidChoice } : {}),
        ...(adjReason ? { reason: adjReason } : {}),
      });
      if (!ok) {
        if (status === 422 && (data?.code === 'LC_DISPOSITION_REQUIRED' || data?.code === 'LC_CASH_PAID_FORK')) { setAdjErr('This landed cost is paid — choose Collect, Cancel, or Write off.'); setCancelBusy(false); return; }
        setAdjErr(data?.error || 'Failed to cancel landed cost.'); setCancelBusy(false); return;
      }
      setCancelFor(null); setCancelImpact(null); setCancelPaidChoice(''); load(); if (onSuccess) onSuccess();
    } catch (e) { setAdjErr(e.message || 'Failed to cancel landed cost.'); }
    finally { setCancelBusy(false); }
  }

  // 206 add: change an LC's allocation method (only while it has un-absorbed holding).
  async function changeMethod(lc, method) {
    setMethodBusy(lc.landed_cost_id);
    try {
      const { ok } = await api.setLcMethod(lc.landed_cost_id, method);
      if (ok) { load(); if (onSuccess) onSuccess(); }
    } catch { /* non-blocking */ }
    finally { setMethodBusy(null); }
  }

  const supplierName = (lc) =>
    effPartnersMap[lc.lc_supplier_id]?.name || effPartnersMap[lc.partner_id]?.name || 'Supplier (unknown)';

  // 369 — PER-LC DISPOSITION (LC-IS-ITS-OWN-DISPOSABLE-UNIT). A decrease/cancel of a PAID LC (cash OR credit)
  // needs an explicit choice: collect (refund_receivable → supplier_refund_holding, collectable via 355-P2),
  // cancel (AP auto-reverse + release the payment to pool), or write off (P&L loss). Default = cancel
  // (non-destructive). Sent to the server as `lc_disposition`. Unpaid LC → cancellation, no selector.
  const DISPO_OPTS = [['collect', 'Collect (supplier owes a refund)'], ['cancel', 'Cancel (reverse & release)'], ['writeoff', 'Write off (P&L loss)']];
  // Show the selector exactly when the server will require it: the decrease touches PAID value (server's
  // needs_disposition = paid split > 0). Falls back to the old cash/any-paid heuristic if the field is absent.
  const hasPaidDecrease = (imp) => !!imp && imp.is_decrease && (imp.needs_disposition != null
    ? !!imp.needs_disposition
    : ((Number(imp.lc_amount_paid) || 0) > 0.005 || imp.nature === 'cash'));

  function openAdjust(lc) {
    setAdjustFor(lc.landed_cost_id);
    setAdjNewRem(String(Number(lc.lc_holding) || 0));  // default = current remaining (no change)
    setAdjImpact(null);
    setAdjPaidChoice('');
    setAdjReason('');
    setAdjErr(null);
    setCancelFor(null);
  }

  // 206 §9 — fetch the financial-impact gate as the user edits the new remaining. The impact endpoint
  // computes new_total = absorbed + new_remaining, tagged paid, net_owed, cash/credit nature, and whether
  // a decrease needs a refund/writeoff choice (cash LC). Debounced by the input value.
  useEffect(() => {
    if (!adjustFor) { setAdjImpact(null); return; }
    const nr = parseFloat(adjNewRem);
    if (!Number.isFinite(nr) || nr < 0) { setAdjImpact(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api.getLcAdjustImpact(adjustFor, nr)
        .then(imp => { if (live) setAdjImpact(imp); })
        .catch(() => { if (live) setAdjImpact(null); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [adjustFor, adjNewRem]);

  // 369 — pre-select the NON-DESTRUCTIVE default (cancel) whenever a paid-LC decrease surfaces the selector, so a
  // no-disposition post can't happen from the UI (the server also hard-rejects a missing one — defense in depth).
  useEffect(() => { if (hasPaidDecrease(adjImpact) && !adjPaidChoice) setAdjPaidChoice('cancel'); }, [adjImpact]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hasPaidDecrease(cancelImpact) && !cancelPaidChoice) setCancelPaidChoice('cancel'); }, [cancelImpact]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function submitAdjust(lc) {
    const nr = parseFloat(adjNewRem);
    if (!Number.isFinite(nr) || nr < 0) { setAdjErr('New remaining must be ≥ 0.'); return; }
    if (hasPaidDecrease(adjImpact) && !adjPaidChoice) {
      setAdjErr('This landed cost is paid — choose Collect, Cancel, or Write off for the reduction.'); return;
    }
    setAdjBusy(true); setAdjErr(null);
    try {
      const { ok, status, data } = await api.adjustLandedCost({
        landed_cost_id: lc.landed_cost_id,
        new_remaining: nr,
        ...(adjPaidChoice ? { lc_disposition: adjPaidChoice } : {}),
        reason: adjReason || undefined,
      });
      if (!ok) {
        if (status === 422 && (data?.code === 'LC_DISPOSITION_REQUIRED' || data?.code === 'LC_CASH_PAID_FORK')) { setAdjErr('This landed cost is paid — choose Collect, Cancel, or Write off.'); setAdjBusy(false); return; }
        setAdjErr(data?.error || 'Failed to adjust landed cost.'); setAdjBusy(false); return;
      }
      setAdjustFor(null); setAdjImpact(null); setAdjPaidChoice(''); setAdjReason('');
      load();
      if (onSuccess) onSuccess();
    } catch (e) {
      setAdjErr(e.message || 'Failed to adjust landed cost.');
    } finally {
      setAdjBusy(false);
    }
  }

  const resetAdjust = () => { setAdjustFor(null); setAdjImpact(null); setAdjPaidChoice(''); setAdjReason(''); };
  const resetCancel = () => { setCancelFor(null); setCancelImpact(null); setCancelPaidChoice(''); setAdjErr(null); };
  const dispoLabels = { collect: t('dCollect'), cancel: t('dCancel'), writeoff: t('dWriteoff') };
  const DispoPick = ({ lc, value, onPick, prefix }) => (
    <div style={{ marginTop: 10 }}>
      <span className="avf-lab">{t('dispoFor', { s: supplierName(lc) })}</span>
      <div className="lc-opts" role="group">
        {DISPO_OPTS.map(([k]) => (
          <button key={k} type="button" data-testid={`${prefix}${k}`} className={`btn sm${value === k ? ' on' : ''}`} aria-pressed={value === k} onClick={() => onPick(k)}>{dispoLabels[k]}</button>
        ))}
      </div>
    </div>
  );

  const renderCard = (lc, i) => {
    const owed = Number(lc.lc_amount_owed) || 0;
    const absorbed = Number(lc.lc_absorbed) || 0;
    const holding = Number(lc.lc_holding) || 0;
    // #44 (§11.4): Total = Absorbed + Holding + Disposed; disposal splits into loss/receivable/reversal.
    // Disposal fields are magnitudes — the engine view guarantees it since 270/db/304 (sum(abs(...))).
    const wo = Number(lc.lc_written_off) || 0;
    const rf = Number(lc.lc_refunded) || 0;
    const cx = Number(lc.lc_cancelled) || 0;
    const disposed = Number(lc.lc_disposed) != null ? Number(lc.lc_disposed) : (wo + rf + cx);
    const isAdjusting = adjustFor === lc.landed_cost_id;
    const isCancelling = cancelFor && cancelFor.landed_cost_id === lc.landed_cost_id;
    const adjNote = (lc.notes || '').replace(/^\[adjustment\]\s*/, '');
    const adjDisabled = adjBusy || !adjImpact || Math.abs(adjImpact.delta) < 0.005;
    const rb = lcRefundBadge(lc);   // same rail authority as OrderTabs, so the two per-LC badges cannot drift apart
    const dispLabel = cx > 0.01 && wo < 0.01 && rf < 0.01 ? t('sCancelled') : rf > 0.01 && wo < 0.01 && cx < 0.01 ? t('sRefundDue') : wo > 0.01 && rf < 0.01 && cx < 0.01 ? t('sWrittenOff') : t('sDisposed');
    return (
      <div key={lc.landed_cost_id || i} className="lc-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
              {/^\[adjustment\]/.test(lc.notes || '')
                ? `${t('adjNo', { n: i + 1 })}${adjNote ? ` — ${adjNote}` : ''}`
                : (lc.notes || t('lcNo', { n: i + 1 }))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              {supplierName(lc)}
              {lc.op_date ? ` · ${formatDate(lc.op_date)}` : ''}
              {lc.lc_due_date ? ` · ${t('due', { d: formatDate(lc.lc_due_date) })}` : ''}
              {lc.lc_overdue ? ` · ${t('overdue')}` : ''}
            </div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            {rb
              ? (rb.label ? <Badge tone={rb.kind === 'released_to_pool' ? 'info' : 'crit'}>{emoText(rb.label)}</Badge> : null)
              : <PaymentBadge p1={lc.lc_payment_status} size="sm" />}
            {owed > 0.01 ? ` · ${t('owed', { v: fmt(owed) })}` : ''}
          </span>
        </div>

        <div className="lc-stats">
          <Stat label={t('sTotal')} value={`${fmt(lc.lc_current)} F`} />
          <Stat label={t('sAbsorbed')} value={`${fmt(absorbed)} F`} tone="good" />
          <Stat label={t('sHolding')} value={`${fmt(holding)} F`} tone={holding > 0.01 ? 'warn' : undefined} />
          {disposed > 0.01 && <Stat label={dispLabel} value={`${fmt(disposed)} F`} tone="crit" />}
          <Stat label={t('sPaid')} value={`${fmt(lc.lc_amount_paid)} F`} />
        </div>

        {!isAdjusting && !isCancelling && (
          <div className="lc-row">
            <button type="button" className="btn sm" data-testid={`lc-adjust-btn-${lc.landed_cost_id}`} onClick={() => openAdjust(lc)}>{lineText(al('adjust'))}</button>
            <button type="button" className="btn sm danger" data-testid={`lc-cancel-btn-${lc.landed_cost_id}`} disabled={holding <= 0.01}
              title={holding <= 0.01 ? t('noHolding') : t('cancelTip')}
              onClick={() => { if (holding > 0.01) startCancel(lc); }}>{lineText(al('cancelLandedCost'))}</button>
            {/* Allocation method — changeable only while un-absorbed (holding > 0); governs how the remaining holding splits on future receipts. */}
            <span className="avf-lab" style={{ marginLeft: 6 }}>{t('method2')}</span>
            <div className="seg" role="group">
              {[['by_value', t('byValue')], ['by_quantity', t('byQtyShort')]].map(([k, lbl]) => {
                const active = (lc.lc_allocation_method || 'by_value') === k;
                const locked = holding <= 0.01;
                const qtyBlocked = k === 'by_quantity' && mixedUnit;   // mixed units → by-quantity invalid
                return (
                  <button key={k} type="button" className={active ? 'on' : ''} aria-pressed={active} disabled={locked || qtyBlocked || methodBusy === lc.landed_cost_id}
                    title={qtyBlocked ? t('mixedTip') : (locked ? t('lockedTip') : t('changeTip'))}
                    onClick={() => { if (!locked && !qtyBlocked && !active) changeMethod(lc, k); }}>{lbl}</button>
                );
              })}
            </div>
          </div>
        )}

        {isAdjusting && (
          <div className="lc-sub">
            {/* 206 §9 — edit the NEW REMAINING; new_total = absorbed + new_remaining. */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Stat label={t('prevTotal')} value={`${fmt(lc.lc_current)} F`} />
              <Stat label={t('alreadyAbs')} value={`${fmt(lc.lc_absorbed)} F`} tone="mut" />
              <Stat label={t('prevRem')} value={`${fmt(lc.lc_holding)} F`} />
              <div style={{ minWidth: 150 }}>
                <Field label={`${t('newRem')} *`}>
                  <TextInput data-testid="lc-adj-newrem" className={`avf-ctl${adjNewRem !== '' && parseFloat(adjNewRem) < 0 ? ' bad' : ''}`} type="number" min="0" step="0.01" value={adjNewRem} onChange={e => setAdjNewRem(e.target.value)} />
                </Field>
              </div>
              <Stat label={t('newTotal')} value={adjImpact ? `${fmt(adjImpact.new_total)} F` : '—'} />
            </div>

            {adjImpact && (
              <div className="lc-impact">
                {adjImpact.is_decrease ? (
                  <>Reduces this landed cost by <strong>{fmt(Math.abs(adjImpact.delta))} F</strong>{' '}
                    {adjImpact.nature === 'credit'
                      ? '— credit LC: payable auto-reverses (cancellation).'
                      : '— cash LC: the supplier was paid, so choose refund or write-off below.'}</>
                ) : adjImpact.is_increase ? (
                  <>Adds <strong>{fmt(adjImpact.delta)} F</strong> to holding (absorbs on the next receipt).{' '}
                    {adjImpact.nature === 'credit' ? 'Payable increases.' : 'Cash out increases.'}</>
                ) : <>No change.</>}
                {/* 403 legibility (402 gap): a freed cancellation credit offsets the new obligation, so "Net owed 598" reads as "1000 new − 402 freed credit". */}
                {(() => {
                  const freed = adjImpact.nature === 'credit' ? (Number(lc.lc_cancelled) || 0) : 0;
                  const legible = freed > 0.01 && adjImpact.is_increase && Math.abs((adjImpact.delta - freed) - adjImpact.net_owed) < 0.02;
                  return (
                    <div className="grid">
                      <span>LC total</span><span className="r">{fmt(adjImpact.new_total)} F</span>
                      <span>Paid (tagged to this LC)</span><span className="r">{fmt(adjImpact.lc_amount_paid)} F</span>
                      {legible && <><span>New obligation</span><span className="r">+{fmt(adjImpact.delta)} F</span>
                        <span>Freed cancellation credit</span><span className="r" style={{ color: 'var(--st-good)' }}>−{fmt(freed)} F</span></>}
                      <span style={{ fontWeight: 700 }}>Net owed</span><span className="r" style={{ fontWeight: 700, color: adjImpact.is_overpaid ? 'var(--st-crit)' : undefined }}>{fmt(adjImpact.net_owed)} F</span>
                    </div>
                  );
                })()}
                {adjImpact.is_overpaid && (
                  adjImpact.nature === 'credit' && (Number(lc.lc_cancelled) || 0) > 0.01
                    ? <div style={{ marginTop: 6, color: 'var(--st-info)', fontWeight: 600 }}>This LC's cancellation credit ({fmt(Number(lc.lc_cancelled) || 0)} F) was released to {supplierName(lc)}'s on-account pool — apply it forward, not a cash refund.</div>
                    : <div style={{ marginTop: 6, color: 'var(--st-warn)', fontWeight: 600 }}><Emo name="warn" /> Overpaid by {fmt(Math.abs(adjImpact.net_owed))} F — the supplier owes a refund (or write it off).</div>
                )}
                {adjImpact.fully_absorbed && adjImpact.is_decrease && (
                  <div style={{ marginTop: 6, color: 'var(--st-warn)' }}>Fully absorbed — nothing left in holding to reduce.</div>
                )}
              </div>
            )}

            {hasPaidDecrease(adjImpact) && <DispoPick lc={lc} value={adjPaidChoice} onPick={setAdjPaidChoice} prefix="lc-dispo-" />}

            <div style={{ marginTop: 10, maxWidth: 320 }}>
              <Field label={t('reason')}>
                <TextInput className="avf-ctl" type="text" value={adjReason} onChange={e => setAdjReason(e.target.value)} placeholder={t('reasonPh')} />
              </Field>
            </div>

            {adjErr && <div className="lc-err">{adjErr}</div>}
            <div className="lc-row">
              <button type="button" className="btn success" data-testid="lc-adj-apply" disabled={adjDisabled} onClick={() => submitAdjust(lc)}>
                {adjBusy ? t('applying') : lineText(al('adjust'))}
              </button>
              <button type="button" className="btn" disabled={adjBusy} onClick={resetAdjust}>{lineText(al('cancel'))}</button>
            </div>
          </div>
        )}

        {isCancelling && (
          <div className="lc-sub">
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--st-crit)', marginBottom: 6 }}>{t('cancelQ')}</div>
            <div className="lc-note warn" style={{ marginBottom: 0 }}>
              <strong>{fmt(holding)} F</strong> in LC Holding will be reversed (remaining → 0). Absorbed
              ({fmt(absorbed)} F) stays in inventory.{' '}
              {cancelImpact ? (hasPaidDecrease(cancelImpact)
                ? 'Paid landed cost — choose a disposition below (never a silent write-off).'
                : 'Unpaid landed cost: payable auto-reverses (cancellation).') : ''}
            </div>
            {hasPaidDecrease(cancelImpact) && <DispoPick lc={lc} value={cancelPaidChoice} onPick={setCancelPaidChoice} prefix="lc-cancel-dispo-" />}
            {adjErr && <div className="lc-err">{adjErr}</div>}
            <div className="lc-row">
              <button type="button" className="btn danger" data-testid="lc-cancel-confirm" disabled={cancelBusy} onClick={() => doCancel(lc)}>
                {cancelBusy ? t('cancelling') : lineText(al('cancelLandedCost'))}
              </button>
              <button type="button" className="btn" disabled={cancelBusy} onClick={resetCancel}>{lineText(al('close'))}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '4px 2px 8px' }}>
      {standalone && (
        <div style={{ marginBottom: 12 }}>
          <Field label={t('orderLbl')}>
            <Combobox
              value={pickedOpId}
              onChange={v => setPickedOpId(v || '')}
              placeholder={`— ${t('orderPh')} —`}
              style={{ display: 'block', width: '100%' }}
              options={operations.map(op => {
                const n = lcCountByOp[op.operation_id];
                return { value: op.operation_id, label: `${op.operation_id} · ${op.partner_name || '—'} · ${[...op.items].join(', ') || '—'} · ${formatDate(op.date)}${n != null ? ` · ${n} LC` : ''}` };
              })}
            />
          </Field>
        </div>
      )}
      {!effectiveOpId ? (
        <div className="lc-note">{t('pickOrder')}</div>
      ) : (
      <>
      {!addOnly && (<>
        <div className="lc-h">{t('existing')}</div>
        {rows === null && <div className="lc-note">{t('loadingList')}</div>}
        {loadErr && <div className="lc-note crit" role="alert">{loadErr}</div>}
        {rows !== null && rows.length === 0 && !loadErr && <div className="lc-note">{t('none')}</div>}
        {(rows || []).map(renderCard)}
      </>)}

      {/* ── ADD NEW (existing form, moved below the list) ── */}
      <div className={addOnly ? 'lc-sec first' : 'lc-sec'}>
        <div className="lc-h">{emoText(al('addLandedCost'))}</div>
        <LandedCostEntryPanel
          open
          embedded
          preselectedOperationId={effectiveOpId}
          gitItems={effGitItems}
          mixedUnitOverride={mixedUnitOverride != null ? mixedUnitOverride : mixedUnit}
          onClose={onClose}
          onSuccess={() => { load(); if (onSuccess) onSuccess(); }}
        />
      </div>
      </>
      )}
    </div>
  );
}
